import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericDialog } from '@/common'
import {
  getOfficialQuarterlyEuaPeriodFromNotificationKey,
  useDismissInAppNotification
} from '@/features/cbam-simulations-v2/hooks/useInAppNotifications'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

type QuarterlyOfficialPriceNotificationDialogProps = {
  isOpen: boolean
  notificationKey: string | null
}

export default function QuarterlyOfficialPriceNotificationDialog({
  isOpen,
  notificationKey
}: QuarterlyOfficialPriceNotificationDialogProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { mutateAsync: dismissNotification, isPending } = useDismissInAppNotification()

  const handleDismiss = useCallback(async () => {
    if (!notificationKey) {
      return
    }
    try {
      await dismissNotification({ notificationKey })
    } catch {
      toast.error(t('common:errors.generic'))
    }
  }, [dismissNotification, notificationKey, t])

  const handleReviewSimulations = useCallback(async () => {
    await handleDismiss()
    navigate('/cbam-simulations')
  }, [handleDismiss, navigate])

  const period = useMemo(() => {
    if (!notificationKey) {
      return null
    }
    return getOfficialQuarterlyEuaPeriodFromNotificationKey(notificationKey)
  }, [notificationKey])

  if (!notificationKey || !period) {
    return null
  }

  const { quarter, year } = period
  const periodKey = `officialQuarterlyPrice.${year}.${quarter}`

  return (
    <GenericDialog
      isOpen={isOpen}
      close={handleDismiss}
      title={t(`inAppNotifications:${periodKey}.title`)}
      subtitle={t(`inAppNotifications:${periodKey}.subtitle`)}
      size="lg"
      variant="warning"
      preventCloseOnOutsideClick
    >
      <div className="space-y-4">
        <p className="text-gray-600 m-0">{t(`inAppNotifications:${periodKey}.body`)}</p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
          <Button variant="subtle" onClick={handleDismiss} disabled={isPending}>
            {t(`inAppNotifications:officialQuarterlyPrice.dismiss`)}
          </Button>
          <Button onClick={handleReviewSimulations} disabled={isPending} isLoading={isPending}>
            {t(`inAppNotifications:officialQuarterlyPrice.reviewSimulations`)}
          </Button>
        </div>
      </div>
    </GenericDialog>
  )
}
